import Phaser from 'phaser';
import type { BossTelegraphState } from '../entities/boss';

/**
 * boss-telegraph.ts — learnable-boss-proto (老闆 2026-06「魔王要可學習」) 的預警渲染。
 *
 * 「預警是合約」:每招在 FIRE 前的 TELEGRAPH 都有**該招專屬、可區分**的預警視覺,
 * 玩家學會「看到這個預警 → 這樣閃」:
 *   - laser  : 從 boss 朝玩家畫一條「紅色瞄準線預演」,線寬 / 亮度隨 progress 漲。
 *   - sniper : boss 砲口「蓄光圓」+ 一條朝玩家準星的短瞄準線,progress 越滿光越亮。
 *
 * 「破綻窗」:RECOVER 期間畫一圈柔和的青色「弱點環」+ boss 微暗提示
 * (微暗交給 scene 既有 alpha;這裡只畫弱點環),獎勵讀懂節奏的玩家集中輸出。
 *
 * 純資料 → Phaser Graphics:輸入 BossTelegraphState + boss/player 座標,
 * 不讀任何 scene 狀態,方便單元測試「該畫什麼」(computeTelegraphPlan)。
 * 學習與遊戲分離:純遊戲機制。
 */

/** 一幀要畫的東西 (純資料;drawBossTelegraph 據此畫,測試直接斷言此 plan)。 */
export interface TelegraphPlan {
  /** 'none' = 不畫 (IDLE/FIRE);'aim-line' = 瞄準線預演;'charge' = 砲口蓄光;'breach' = 破綻弱點環。 */
  kind: 'none' | 'aim-line' | 'charge' | 'breach';
  /** 預演瞄準線 / 蓄光的顏色 (laser=紅, sniper=黃, breach=青)。 */
  color: number;
  /** 0→1 強度 (telegraph 用 telegraphProgress;breach 用 1-recoverProgress 漸隱)。 */
  intensity: number;
}

/** laser 預警 = 紅色。 */
export const TELEGRAPH_COLOR_LASER = 0xff3030;
/** sniper 預警 = 黃色 (砲口蓄光)。 */
export const TELEGRAPH_COLOR_SNIPER = 0xffd54f;
/** 破綻弱點環 = 青色 (跟攻擊紅/黃明顯區分,讀作「安全/該打了」)。 */
export const BREACH_COLOR = 0x4dd0e1;

/**
 * 純函式:由 telegraph 狀態 + 招式 id 決定「這一幀該畫什麼」。
 * 把渲染決策抽出來,讓「laser→aim-line / sniper→charge / RECOVER→breach」這條
 * 可學習合約有單元測試覆蓋 (不需 Phaser)。
 */
export function computeTelegraphPlan(state: BossTelegraphState): TelegraphPlan {
  if (state.phase === 'TELEGRAPH') {
    if (state.moveId === 'laser') {
      return { kind: 'aim-line', color: TELEGRAPH_COLOR_LASER, intensity: state.telegraphProgress };
    }
    // 預設 (含 sniper):砲口蓄光。
    return { kind: 'charge', color: TELEGRAPH_COLOR_SNIPER, intensity: state.telegraphProgress };
  }
  if (state.phase === 'RECOVER' && state.vulnerable) {
    // 破綻環隨 recoverProgress 漸隱 (一開始最亮 → 提醒「現在打」)。
    return { kind: 'breach', color: BREACH_COLOR, intensity: 1 - state.recoverProgress };
  }
  return { kind: 'none', color: 0, intensity: 0 };
}

/**
 * 把 TelegraphPlan 畫到 Phaser Graphics。每幀 clear 後重畫。
 *
 * @param gfx       Graphics handle (caller 重複用同一個、每幀 clear)。
 * @param plan      computeTelegraphPlan 的結果。
 * @param bossX/Y   boss 中心。
 * @param playerX/Y 玩家中心 (aim-line 指向用)。
 * @param bossRadius boss 半徑 (蓄光 / 破綻環大小用)。
 */
export function drawBossTelegraph(
  gfx: Phaser.GameObjects.Graphics,
  plan: TelegraphPlan,
  bossX: number,
  bossY: number,
  playerX: number,
  playerY: number,
  bossRadius: number,
): void {
  gfx.clear();
  if (plan.kind === 'none' || plan.intensity <= 0) return;

  if (plan.kind === 'aim-line') {
    // 紅色瞄準線預演:從 boss 朝玩家延伸一條漸亮、漸粗的虛擬光束 (還沒傷害)。
    const ang = Math.atan2(playerY - bossY, playerX - bossX);
    const len = 1200; // 夠長貫穿畫面
    const ex = bossX + Math.cos(ang) * len;
    const ey = bossY + Math.sin(ang) * len;
    const width = 2 + plan.intensity * 10; // 2→12px 漸粗
    gfx.lineStyle(width, plan.color, 0.25 + plan.intensity * 0.55);
    gfx.beginPath();
    gfx.moveTo(bossX, bossY);
    gfx.lineTo(ex, ey);
    gfx.strokePath();
    return;
  }

  if (plan.kind === 'charge') {
    // 砲口蓄光圓 (隨 progress 變大變亮) + 一條短瞄準線指向玩家準星。
    const r = bossRadius * (0.25 + plan.intensity * 0.5);
    gfx.fillStyle(plan.color, 0.3 + plan.intensity * 0.5);
    gfx.fillCircle(bossX, bossY, r);
    const ang = Math.atan2(playerY - bossY, playerX - bossX);
    const len = bossRadius * 2.2;
    gfx.lineStyle(2 + plan.intensity * 3, plan.color, 0.4 + plan.intensity * 0.5);
    gfx.beginPath();
    gfx.moveTo(bossX, bossY);
    gfx.lineTo(bossX + Math.cos(ang) * len, bossY + Math.sin(ang) * len);
    gfx.strokePath();
    return;
  }

  // breach:破綻弱點環 (柔和青色脈動環,讀作「現在該集中輸出」)。
  const ringR = bossRadius + 14;
  gfx.lineStyle(4, plan.color, 0.3 + plan.intensity * 0.5);
  gfx.strokeCircle(bossX, bossY, ringR);
}
